import lodash from 'lodash'

import getFeeAmount from './get-fee-amount.js'
import getNamesOfTokensTransferredByServerTx from './get-names-of-tokens-transferred-by-server-tx.js'
import getTransfersByTokenName from './get-transfers-by-token-name.js'
import getValueOfTransfers from './get-value-of-transfers.js'
import isConfirmedServerTx from './is-confirmed-server-tx.js'
import isConsideredSentTokenTx from './is-considered-sent-token-tx.js'

// This function takes a server transaction object fetched from magnifier,
// and transforms it into Tx models to update the exodus state.

export default function getLogItemsFromServerTx({
  serverTx,
  asset,
  ourWalletAddress,
  tokensByAddress,
  assets,
}) {
  const confirmations = isConfirmedServerTx(serverTx) ? 1 : 0
  const date = new Date(parseInt(serverTx.timestamp, 16) * 1000) // included even for unconfirmed txs
  const txId = serverTx.hash
  const nonce = parseInt(serverTx.nonce, 16)
  const gasLimit = parseInt(serverTx.gas, 16)
  const error = serverTx.error || (serverTx.status === false ? 'Failed' : null)
  const feeAmount = getFeeAmount(asset, serverTx)
  const ethereumTransfers = [serverTx, ...(serverTx.internal || [])]
  const tokenTransfersByTokenName = getTransfersByTokenName(serverTx.erc20 || [], tokensByAddress)
  const toAddress = tryFindExternalRecipient(ethereumTransfers, ourWalletAddress)
  const ourWalletWasSender = serverTx.from === ourWalletAddress
  const METHOD_ID_LENGTH = 10
  const methodId = serverTx.data && {
    methodId: serverTx.data.slice(0, Math.max(0, METHOD_ID_LENGTH)),
  }
  const data = serverTx.data || '0x'
  const logItemCommonProperties = {
    confirmations,
    date,
    error,
    txId,
    dropped: false,
  }

  const logItemsForServerTxEntries = []

  {
    const sendingTransferPresent = ethereumTransfers.some(({ from }) => from === ourWalletAddress)
    const receivingTransferPresent = ethereumTransfers.some(({ to }) => to === ourWalletAddress)
    const nftTransferPresent = isNftTransfer({ serverTx, ourWalletAddress })

    if (sendingTransferPresent || receivingTransferPresent || nftTransferPresent) {
      const coinAmount = getValueOfTransfers(ourWalletAddress, asset, ethereumTransfers)
      const selfSend = isSelfSendTx({
        coinAmount,
        ourWalletWasSender,
        sendingTransferPresent,
        receivingTransferPresent,
      })
      let sent
      if (sendingTransferPresent) {
        sent = serverTx.internal
          .map(({ from, to, value }) => {
            if (from === ourWalletAddress) {
              const amount = asset.currency.baseUnit(value).toDefaultString({ unit: true })
              return { address: to, amount }
            }
          })
          .filter(Boolean)
      }

      logItemsForServerTxEntries.push([
        asset.name,
        {
          ...logItemCommonProperties,
          coinAmount,
          coinName: asset.name,
          data: {
            data,
            nonce,
            gasLimit,
            ...methodId,
            ...(sent?.length > 0 ? { sent } : undefined),
          },
          ...(ourWalletWasSender
            ? { from: [], to: toAddress, feeAmount, feeCoinName: asset.feeAsset.name }
            : { from: [serverTx.from] }),
          selfSend,
          tokens: getNamesOfTokensTransferredByServerTx({
            asset,
            tokensByAddress,
            serverTx,
            ourWalletAddress,
          }),
          currencies: {
            [asset.name]: asset.currency,
            [asset.feeAsset.name]: asset.feeAsset.currency,
          },
        },
      ])
    }
  }

  // handle erc20
  Object.entries(tokenTransfersByTokenName).forEach(([tokenName, tokenTransfers]) => {
    const sendingTransferPresent = tokenTransfers.some(({ from }) => from === ourWalletAddress)
    const receivingTransferPresent = tokenTransfers.some(({ to }) => to === ourWalletAddress)
    if (!sendingTransferPresent && !receivingTransferPresent) return

    const token = assets[tokenName]
    const tokenTransferToAddress = tryFindExternalRecipient(tokenTransfers, ourWalletAddress)
    const coinAmount = getValueOfTransfers(
      ourWalletAddress,
      token,
      lodash.filter(tokenTransfers, { events: confirmations > 0 })
    )
    const tokenFromAddresses = lodash.uniq(
      tokenTransfers.filter(({ to }) => to === ourWalletAddress).map(({ from }) => from)
    )

    const isConsideredSent = isConsideredSentTokenTx({
      coinAmount,
      ourWalletWasSender,
      sendingTransferPresent,
      receivingTransferPresent,
    })
    const selfSend = isSelfSendTx({
      coinAmount,
      ourWalletWasSender,
      sendingTransferPresent,
      receivingTransferPresent,
    })

    logItemsForServerTxEntries.push([
      tokenName,
      {
        ...logItemCommonProperties,
        coinAmount,
        coinName: tokenName,
        data: { data, nonce, gasLimit, ...methodId },
        ...(isConsideredSent
          ? { from: [], to: tokenTransferToAddress, feeAmount, feeCoinName: asset.feeAsset.name }
          : { from: tokenFromAddresses }),
        selfSend,
        currencies: {
          [token.name]: token.currency,
          [token.feeAsset.name]: token.feeAsset.currency,
        },
      },
    ])
  })

  return Object.fromEntries(logItemsForServerTxEntries)
}

const tryFindExternalRecipient = (transfers, ourWalletAddress) =>
  transfers.find(({ to }) => to !== ourWalletAddress)?.to || ourWalletAddress

function isSelfSendTx({
  coinAmount,
  ourWalletWasSender,
  sendingTransferPresent,
  receivingTransferPresent,
}) {
  return (
    coinAmount.isZero && sendingTransferPresent && receivingTransferPresent && ourWalletWasSender
  )
}

function isNftTransfer({ serverTx, ourWalletAddress }) {
  if (!Array.isArray(serverTx.erc721) || serverTx.erc721.length === 0) return false

  return serverTx.erc721.some(
    (transfer) => transfer.to === ourWalletAddress || transfer.from === ourWalletAddress
  )
}
